import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomerPaymentTransactionModal from "../../../../components/CustomerPaymentTransactionModal";
import VendorPaymentTransactionModal from "../../../../components/VendorPaymentTransactionModal";
import CustomerTransactionViewModal from "../../../../components/CustomerTransactionViewModal";
import VendorTransactionViewModal from "../../../../components/VendorTransactionViewModal";
import AddBankAccountModal from "../../../../components/AddBankAccountModal";
import {
  ErrorBanner,
  ReviewRequestModal,
  useToast,
} from "../../../../shared/presentation/components";
import { useErrorHandling } from "../../../../shared/presentation/hooks";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  ACTION_CODES,
  MODULE_CODES,
} from "../../../../shared/constants/permissions";
import { useMinimumLoading } from "../../../../shared/presentation/hooks/useMinimumLoading";
import { usePersistedSort } from "../../../../shared/presentation/hooks/usePersistedSort";
import {
  DateRangeValue,
  formatDateForApi,
} from "../../../../shared/presentation/utils/dateRangeFilters";
import { usePersistedDateRange } from "../../../../shared/presentation/hooks/usePersistedDateRange";
import { usePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";
import {
  CashBankAccountEntity,
  CashBankSortField,
  CashBankSortOrder,
  CashBankTransactionEntity,
  CashBankTransactionStatus,
  SplitCashBankTransactionUseCase,
} from "../../domain";
import { CashAndBankRepositoryImpl } from "../../data";
import {
  useCashBankAccounts,
  useCashBankSummary,
  useCashBankTransactions,
} from "../hooks";
import { CashBankHeader } from "./CashBankHeader";
import { CashBankAccountTabs } from "./CashBankAccountTabs";
import { CashBankSummaryCards } from "./CashBankSummaryCards";
import { CashBankTransactionSection } from "./CashBankTransactionSection";
import { CashBankSortState } from "./CashBankTransactionToolbar";
import { SplitTransactionModal } from "./SplitTransactionModal";

const ITEMS_PER_PAGE = 10;
const splitTransactionUseCase = new SplitCashBankTransactionUseCase(
  new CashAndBankRepositoryImpl(),
);
const CASH_BANK_TRANSACTION_STATUSES: CashBankTransactionStatus[] = [
  "all",
  "pending",
  "under_review",
  "reconciled",
];
type ListLoadingReason = "initial" | "pagination" | "refresh" | null;

const deserializeAccountId = (raw: string, fallback: number | null) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "number" && Number.isInteger(parsed) && parsed > 0
    ? parsed
    : fallback;
};

const deserializeTransactionStatus = (
  raw: string,
  fallback: CashBankTransactionStatus,
) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    CASH_BANK_TRANSACTION_STATUSES.includes(parsed as CashBankTransactionStatus)
    ? (parsed as CashBankTransactionStatus)
    : fallback;
};

const toLegacyBankAccounts = (accounts: CashBankAccountEntity[]) =>
  accounts.map((account) => ({
    ...account,
    id: account.id.toString(),
  }));

const getModalAccountType = (
  account: CashBankAccountEntity | null,
): "bank" | "credit" | undefined => {
  if (!account) {
    return undefined;
  }

  return account.account_type === "credit" ||
    account.account_type === "credit_card"
    ? "credit"
    : "bank";
};

const CashBankSection: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const {
    errors,
    setApiErrors,
    clearErrors,
    hasErrors: checkHasErrors,
  } = useErrorHandling();

  const { getPermissionInfo, hasPermission } = usePermissions();
  const canMatch = hasPermission(
    MODULE_CODES.BANK_TRANSACTION,
    ACTION_CODES.MATCH,
  );
  const canUnmatch = hasPermission(
    MODULE_CODES.BANK_TRANSACTION,
    ACTION_CODES.UNMATCH,
  );
  const splitPermission = getPermissionInfo(
    MODULE_CODES.BANK_TRANSACTION,
    ACTION_CODES.SPLIT_TRANSACTION_AMOUNT,
  );
  const canSplit = splitPermission.granted;
  const canAddBankAccount = hasPermission(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.CREATE,
  );
  const canUpdateBankAccount = hasPermission(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.UPDATE,
  );
  const canDeleteBankAccount = hasPermission(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.DELETE,
  );
  const canManageBankAccount = canUpdateBankAccount || canDeleteBankAccount;

  const [selectedAccountId, setSelectedAccountId] = usePersistedState<
    number | null
  >("cashBank.selectedAccountId", null, {
    deserialize: deserializeAccountId,
  });
  const [dateRange, setDateRange] = usePersistedDateRange("cashBank.dateRange");
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);

  const [customerStatus, setCustomerStatus] =
    usePersistedState<CashBankTransactionStatus>(
      "cashBank.customer.status",
      "all",
      { deserialize: deserializeTransactionStatus },
    );
  const [vendorStatus, setVendorStatus] =
    usePersistedState<CashBankTransactionStatus>(
      "cashBank.vendor.status",
      "all",
      { deserialize: deserializeTransactionStatus },
    );
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  const [customerPage, setCustomerPage] = useState(1);
  const [vendorPage, setVendorPage] = useState(1);
  const [customerLoadingReason, setCustomerLoadingReason] =
    useState<ListLoadingReason>("initial");
  const [vendorLoadingReason, setVendorLoadingReason] =
    useState<ListLoadingReason>("initial");
  const [hasLoadedCustomerTransactions, setHasLoadedCustomerTransactions] =
    useState(false);
  const [hasLoadedVendorTransactions, setHasLoadedVendorTransactions] =
    useState(false);
  const [customerSort, setCustomerSort] = usePersistedSort<CashBankSortState>(
    "cashBank.customer",
    {
      by: "date",
      order: "desc",
      label: "Date: Newest First",
    },
  );
  const [vendorSort, setVendorSort] = usePersistedSort<CashBankSortState>(
    "cashBank.vendor",
    {
      by: "date",
      order: "desc",
      label: "Date: Newest First",
    },
  );

  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [selectedCustomerTransaction, setSelectedCustomerTransaction] =
    useState<CashBankTransactionEntity | null>(null);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [selectedVendorTransaction, setSelectedVendorTransaction] =
    useState<CashBankTransactionEntity | null>(null);
  const [isCustomerViewModalOpen, setIsCustomerViewModalOpen] = useState(false);
  const [selectedCustomerViewTransaction, setSelectedCustomerViewTransaction] =
    useState<CashBankTransactionEntity | null>(null);
  const [isVendorViewModalOpen, setIsVendorViewModalOpen] = useState(false);
  const [selectedVendorViewTransaction, setSelectedVendorViewTransaction] =
    useState<CashBankTransactionEntity | null>(null);
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [selectedSplitTransaction, setSelectedSplitTransaction] =
    useState<CashBankTransactionEntity | null>(null);
  const [selectedSplitTransactionType, setSelectedSplitTransactionType] =
    useState<"customer" | "vendor" | null>(null);
  const [isSplitReviewModalOpen, setIsSplitReviewModalOpen] = useState(false);
  const [splitReviewRequest, setSplitReviewRequest] = useState<{
    transaction: CashBankTransactionEntity;
    transactionType: "customer" | "vendor";
    splits: { amount: number; description: string }[];
  } | null>(null);

  const customerPaymentsRef = useRef<HTMLDivElement>(null);
  const vendorPaymentsRef = useRef<HTMLDivElement>(null);
  const customerLoadingStartedRef = useRef(false);
  const vendorLoadingStartedRef = useRef(false);

  const {
    accounts,
    setAccounts,
    loading: accountsLoading,
    fetchAccounts,
  } = useCashBankAccounts();
  const {
    summary,
    setSummary,
    loading: summaryLoading,
    fetchSummary,
  } = useCashBankSummary();
  const {
    transactions: customerTransactionList,
    setTransactions: setCustomerTransactionList,
    pagination: customerPagination,
    loading: customerTransactionsLoading,
    error: customerTransactionsError,
    fetchTransactions: fetchCustomerTransactionList,
  } = useCashBankTransactions();

  const emptySummary = useMemo(() => ({ count: 0, total_amount: 0 }), []);
  const {
    transactions: vendorTransactionList,
    setTransactions: setVendorTransactionList,
    pagination: vendorPagination,
    loading: vendorTransactionsLoading,
    error: vendorTransactionsError,
    fetchTransactions: fetchVendorTransactionList,
  } = useCashBankTransactions();

  const selectedAccount = useMemo(
    () =>
      selectedAccountId
        ? (accounts.find((account) => account.id === selectedAccountId) ?? null)
        : null,
    [accounts, selectedAccountId],
  );
  const selectedZohoBankAccountId = selectedAccount?.zoho_bankaccountid ?? "";
  const selectedAccountSummary = useMemo(
    () => ({
      pending_customer_payments:
        summary?.pending_customer_payments ??
        selectedAccount?.pending_customer_payments ??
        emptySummary,
      pending_vendor_payments:
        summary?.pending_vendor_payments ??
        selectedAccount?.pending_vendor_payments ??
        emptySummary,
    }),
    [emptySummary, summary, selectedAccount],
  );
  const legacyBankAccounts = useMemo(
    () => toLegacyBankAccounts(accounts),
    [accounts],
  );
  const lockedAccountMessage =
    canManageBankAccount &&
    selectedAccount?.pending_review?.status === "pending" &&
    (selectedAccount.pending_review.action_code === "update" ||
      selectedAccount.pending_review.action_code === "delete")
      ? "Account update/delete is under review"
      : null;
  const dateFilterParams = useMemo(
    () => ({
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
    }),
    [dateRange.from, dateRange.preset, dateRange.to],
  );

  const fetchCustomerTransactions = useCallback(
    async (page: number) => {
      if (!selectedAccount) {
        return;
      }

      await fetchCustomerTransactionList({
        accountId: selectedAccount.zoho_bankaccountid,
        type: "credit",
        status: customerStatus,
        limit: ITEMS_PER_PAGE,
        offset: Math.max(page - 1, 0) * ITEMS_PER_PAGE,
        sortBy: customerSort.by,
        sortOrder: customerSort.order,
        search: customerSearchTerm.trim() || undefined,
        ...dateFilterParams,
      });
    },
    [
      customerSearchTerm,
      customerSort.by,
      customerSort.order,
      customerStatus,
      dateFilterParams,
      fetchCustomerTransactionList,
      selectedAccount,
    ],
  );

  const fetchVendorTransactions = useCallback(
    async (page: number) => {
      if (!selectedAccount) {
        return;
      }

      await fetchVendorTransactionList({
        accountId: selectedAccount.zoho_bankaccountid,
        type: "debit",
        status: vendorStatus,
        limit: ITEMS_PER_PAGE,
        offset: Math.max(page - 1, 0) * ITEMS_PER_PAGE,
        sortBy: vendorSort.by,
        sortOrder: vendorSort.order,
        search: vendorSearchTerm.trim() || undefined,
        ...dateFilterParams,
      });
    },
    [
      dateFilterParams,
      fetchVendorTransactionList,
      selectedAccount,
      vendorSearchTerm,
      vendorSort.by,
      vendorSort.order,
      vendorStatus,
    ],
  );

  const refetchAccounts = useCallback(async () => {
    try {
      clearErrors();
      const nextAccounts = await fetchAccounts();
      setSelectedAccountId((previousAccountId) => {
        if (nextAccounts.length === 0) {
          return null;
        }

        if (
          previousAccountId &&
          nextAccounts.some((account) => account.id === previousAccountId)
        ) {
          return previousAccountId;
        }

        return nextAccounts[0].id;
      });
    } catch (error) {
      setApiErrors(error);
      showError("Failed to load bank accounts");
    }
  }, [
    clearErrors,
    fetchAccounts,
    setApiErrors,
    setSelectedAccountId,
    showError,
  ]);

  useEffect(() => {
    refetchAccounts();
  }, [refetchAccounts]);

  useEffect(() => {
    if (customerTransactionsLoading) {
      customerLoadingStartedRef.current = true;
      return;
    }

    if (customerLoadingStartedRef.current) {
      customerLoadingStartedRef.current = false;
      setHasLoadedCustomerTransactions(true);
      setCustomerLoadingReason(null);
    }
  }, [customerTransactionsLoading]);

  useEffect(() => {
    if (vendorTransactionsLoading) {
      vendorLoadingStartedRef.current = true;
      return;
    }

    if (vendorLoadingStartedRef.current) {
      vendorLoadingStartedRef.current = false;
      setHasLoadedVendorTransactions(true);
      setVendorLoadingReason(null);
    }
  }, [vendorTransactionsLoading]);

  useEffect(() => {
    if (!selectedAccount) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => {
        fetchCustomerTransactions(customerPage).catch((error) => {
          setApiErrors(error);
          showError("Failed to load customer payment transactions");
        });
      },
      customerSearchTerm.trim() ? 500 : 0,
    );

    return () => window.clearTimeout(timeoutId);
  }, [
    customerSearchTerm,
    customerStatus,
    customerPage,
    customerSort.by,
    customerSort.order,
    fetchCustomerTransactions,
    selectedAccount,
    setApiErrors,
    showError,
  ]);

  useEffect(() => {
    if (!selectedAccount) {
      return;
    }

    fetchSummary({
      accountId: selectedAccount.zoho_bankaccountid,
      ...dateFilterParams,
    }).catch((error) => {
      setApiErrors(error);
      showError("Failed to load pending payment summary");
    });
  }, [
    dateFilterParams,
    fetchSummary,
    selectedAccount,
    setApiErrors,
    showError,
  ]);

  useEffect(() => {
    if (!selectedAccount) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => {
        fetchVendorTransactions(vendorPage).catch((error) => {
          setApiErrors(error);
          showError("Failed to load vendor payment transactions");
        });
      },
      vendorSearchTerm.trim() ? 500 : 0,
    );

    return () => window.clearTimeout(timeoutId);
  }, [
    fetchVendorTransactions,
    selectedAccount,
    setApiErrors,
    showError,
    vendorPage,
    vendorSearchTerm,
    vendorSort.by,
    vendorSort.order,
    vendorStatus,
  ]);

  const handleAccountSelect = (accountId: number) => {
    if (accountId === selectedAccountId) return;

    setIsSwitchingAccount(true);
    setCustomerLoadingReason("initial");
    setVendorLoadingReason("initial");
    setSelectedAccountId(accountId);
    setCustomerPage(1);
    setVendorPage(1);
  };

  useEffect(() => {
    if (
      isSwitchingAccount &&
      !customerTransactionsLoading &&
      !vendorTransactionsLoading
    ) {
      setIsSwitchingAccount(false);
    }
  }, [
    customerTransactionsLoading,
    isSwitchingAccount,
    vendorTransactionsLoading,
  ]);

  const updateAccountPendingSummary = (
    accountId: number | null,
    type: "customer" | "vendor",
    amount: number,
    direction: "increase" | "decrease",
  ) => {
    if (!accountId) {
      return;
    }

    setAccounts((previousAccounts) =>
      previousAccounts.map((account) => {
        if (account.id !== accountId) {
          return account;
        }

        const key =
          type === "customer"
            ? "pending_customer_payments"
            : "pending_vendor_payments";
        const current = account[key];
        const delta = direction === "increase" ? 1 : -1;
        const amountDelta = direction === "increase" ? amount : -amount;

        return {
          ...account,
          [key]: {
            count: Math.max(0, current.count + delta),
            total_amount: Math.max(0, current.total_amount + amountDelta),
          },
        };
      }),
    );
    setSummary((previousSummary) => {
      if (!previousSummary) {
        return previousSummary;
      }

      const key =
        type === "customer"
          ? "pending_customer_payments"
          : "pending_vendor_payments";
      const current = previousSummary[key];
      const delta = direction === "increase" ? 1 : -1;
      const amountDelta = direction === "increase" ? amount : -amount;

      return {
        ...previousSummary,
        [key]: {
          count: Math.max(0, current.count + delta),
          total_amount: Math.max(0, current.total_amount + amountDelta),
        },
      };
    });
  };

  const handleCustomerMatched = (transactionId: string, amount: number) => {
    setCustomerTransactionList((previousTransactions) =>
      previousTransactions.map((transaction) =>
        transaction.transaction_id === transactionId
          ? { ...transaction, user_status: "Reconciled" }
          : transaction,
      ),
    );
    updateAccountPendingSummary(
      selectedAccountId,
      "customer",
      amount,
      "decrease",
    );
    fetchCustomerTransactions(customerPage).catch(() => {});
    showSuccess("Customer payment transaction reconciled successfully");
  };

  const handleVendorMatched = (transactionId: string, amount: number) => {
    setVendorTransactionList((previousTransactions) =>
      previousTransactions.map((transaction) =>
        transaction.transaction_id === transactionId
          ? { ...transaction, user_status: "Reconciled" }
          : transaction,
      ),
    );
    updateAccountPendingSummary(
      selectedAccountId,
      "vendor",
      amount,
      "decrease",
    );
    fetchVendorTransactions(vendorPage).catch(() => {});
    showSuccess("Vendor payment transaction reconciled successfully");
  };

  const handleCustomerUnmatched = (transactionId: string, amount: number) => {
    setIsCustomerViewModalOpen(false);
    setCustomerTransactionList((previousTransactions) =>
      previousTransactions.filter(
        (transaction) => transaction.transaction_id !== transactionId,
      ),
    );
    updateAccountPendingSummary(
      selectedAccountId,
      "customer",
      amount,
      "increase",
    );
    fetchCustomerTransactions(customerPage).catch(() => {});
    showSuccess("Transaction moved back to pending successfully");
  };

  const handleVendorUnmatched = (transactionId: string, amount: number) => {
    setIsVendorViewModalOpen(false);
    setVendorTransactionList((previousTransactions) =>
      previousTransactions.filter(
        (transaction) => transaction.transaction_id !== transactionId,
      ),
    );
    updateAccountPendingSummary(
      selectedAccountId,
      "vendor",
      amount,
      "increase",
    );
    fetchVendorTransactions(vendorPage).catch(() => {});
    showSuccess("Transaction moved back to pending successfully");
  };

  const handleSplitTransaction = async (
    splits: { amount: number; description: string }[],
  ) => {
    if (!selectedSplitTransaction || !selectedSplitTransactionType) {
      throw new Error("No transaction selected for split");
    }

    if (splitPermission.requiresReview) {
      if (!selectedSplitTransaction.id) {
        throw new Error("Transaction ID is required to request split review");
      }

      setSplitReviewRequest({
        transaction: selectedSplitTransaction,
        transactionType: selectedSplitTransactionType,
        splits,
      });
      setIsSplitReviewModalOpen(true);
      return;
    }

    await splitTransactionUseCase.execute({
      transactionId: selectedSplitTransaction.transaction_id,
      splits,
    });

    if (selectedSplitTransactionType === "customer") {
      setCustomerLoadingReason("refresh");
      await fetchCustomerTransactions(customerPage);
    } else {
      setVendorLoadingReason("refresh");
      await fetchVendorTransactions(vendorPage);
    }

    await Promise.all([
      fetchSummary({
        accountId: selectedAccount?.zoho_bankaccountid,
        ...dateFilterParams,
      }).catch(() => {}),
      refetchAccounts().catch(() => {}),
    ]);

    showSuccess("Transaction split successfully");
  };

  const refreshSplitReviewTransactionList = async () => {
    const request = splitReviewRequest;
    if (!request) {
      return;
    }

    if (request.transactionType === "customer") {
      setCustomerLoadingReason("refresh");
      await fetchCustomerTransactions(customerPage);
    } else {
      setVendorLoadingReason("refresh");
      await fetchVendorTransactions(vendorPage);
    }
  };

  const handleCustomerPageChange = (page: number) => {
    if (page === customerPage) return;

    setCustomerLoadingReason("pagination");
    setCustomerPage(page);
  };

  const handleVendorPageChange = (page: number) => {
    if (page === vendorPage) return;

    setVendorLoadingReason("pagination");
    setVendorPage(page);
  };

  const handleDateRangeChange = (nextDateRange: DateRangeValue) => {
    if (
      nextDateRange.preset === dateRange.preset &&
      nextDateRange.from?.getTime() === dateRange.from?.getTime() &&
      nextDateRange.to?.getTime() === dateRange.to?.getTime()
    ) {
      return;
    }

    setCustomerLoadingReason("refresh");
    setVendorLoadingReason("refresh");
    setDateRange(nextDateRange);
    setCustomerPage(1);
    setVendorPage(1);
  };

  const handleCustomerStatusChange = (value: CashBankTransactionStatus) => {
    if (value === customerStatus) return;

    setCustomerLoadingReason("refresh");
    setCustomerStatus(value);
    setCustomerPage(1);
  };

  const handleVendorStatusChange = (value: CashBankTransactionStatus) => {
    if (value === vendorStatus) return;

    setVendorLoadingReason("refresh");
    setVendorStatus(value);
    setVendorPage(1);
  };

  const handleCustomerSearchTermChange = (value: string) => {
    if (value === customerSearchTerm) return;

    setCustomerLoadingReason("refresh");
    setCustomerSearchTerm(value);
    setCustomerPage(1);
  };

  const handleVendorSearchTermChange = (value: string) => {
    if (value === vendorSearchTerm) return;

    setVendorLoadingReason("refresh");
    setVendorSearchTerm(value);
    setVendorPage(1);
  };

  const handleCustomerSortChange = (
    sortBy: CashBankSortField,
    sortOrder: CashBankSortOrder,
    label: string,
  ) => {
    if (
      sortBy === customerSort.by &&
      sortOrder === customerSort.order &&
      label === customerSort.label
    ) {
      return;
    }

    setCustomerLoadingReason("refresh");
    setCustomerSort({ by: sortBy, order: sortOrder, label });
    setCustomerPage(1);
  };

  const handleVendorSortChange = (
    sortBy: CashBankSortField,
    sortOrder: CashBankSortOrder,
    label: string,
  ) => {
    if (
      sortBy === vendorSort.by &&
      sortOrder === vendorSort.order &&
      label === vendorSort.label
    ) {
      return;
    }

    setVendorLoadingReason("refresh");
    setVendorSort({ by: sortBy, order: sortOrder, label });
    setVendorPage(1);
  };

  const isInitialLoading = accountsLoading && accounts.length === 0;
  const isSummaryLoading =
    isInitialLoading || isSwitchingAccount || summaryLoading;
  const isCustomerListLoading =
    isInitialLoading ||
    isSwitchingAccount ||
    customerTransactionsLoading ||
    customerLoadingReason === "refresh";
  const isVendorListLoading =
    isInitialLoading ||
    isSwitchingAccount ||
    vendorTransactionsLoading ||
    vendorLoadingReason === "refresh";
  const isCustomerTableLoading =
    isInitialLoading ||
    isSwitchingAccount ||
    customerLoadingReason === "pagination" ||
    (customerTransactionsLoading && !hasLoadedCustomerTransactions);
  const isVendorTableLoading =
    isInitialLoading ||
    isSwitchingAccount ||
    vendorLoadingReason === "pagination" ||
    (vendorTransactionsLoading && !hasLoadedVendorTransactions);
  const smoothInitialLoading = useMinimumLoading(isInitialLoading);
  const smoothSummaryLoading = useMinimumLoading(isSummaryLoading);
  const smoothCustomerListLoading = useMinimumLoading(isCustomerListLoading);
  const smoothVendorListLoading = useMinimumLoading(isVendorListLoading);
  const smoothCustomerTableLoading = useMinimumLoading(isCustomerTableLoading);
  const smoothVendorTableLoading = useMinimumLoading(isVendorTableLoading);

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 overflow-auto bg-default-background px-8 py-8">
      <div className="flex w-full flex-col items-start gap-6">
        <CashBankHeader
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          canAddBankAccount={canAddBankAccount}
          onAddBankAccount={() => setIsAddAccountModalOpen(true)}
          lockedAccountMessage={lockedAccountMessage}
        />

        {!checkHasErrors() && (
          <CashBankAccountTabs
            accounts={accounts}
            selectedAccountId={selectedAccountId}
            onSelectAccount={handleAccountSelect}
            loading={smoothInitialLoading}
          />
        )}

        {!checkHasErrors() && (selectedAccount || smoothInitialLoading) && (
          <CashBankSummaryCards
            customerSummary={selectedAccountSummary.pending_customer_payments}
            vendorSummary={selectedAccountSummary.pending_vendor_payments}
            loading={smoothSummaryLoading}
            onViewCustomerPending={() => {
              handleCustomerStatusChange("pending");
              customerPaymentsRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
            onViewVendorPending={() => {
              handleVendorStatusChange("pending");
              vendorPaymentsRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
          />
        )}

        {checkHasErrors() && (
          <ErrorBanner errors={errors} title="Oops! Something went wrong:" />
        )}

        {!checkHasErrors() && (selectedAccount || smoothInitialLoading) && (
          <>
            <CashBankTransactionSection
              ref={customerPaymentsRef}
              title="Customer Payments (Money In)"
              type="credit"
              status={customerStatus}
              onStatusChange={handleCustomerStatusChange}
              searchTerm={customerSearchTerm}
              onSearchTermChange={handleCustomerSearchTermChange}
              sort={customerSort}
              onSortChange={handleCustomerSortChange}
              transactions={customerTransactionList}
              pagination={customerPagination}
              loading={smoothCustomerListLoading}
              tableLoading={smoothCustomerTableLoading}
              error={customerTransactionsError}
              canMatch={canMatch}
              canUnmatch={canUnmatch}
              canSplit={canSplit}
              onRefresh={() => {
                setCustomerLoadingReason("refresh");
                fetchCustomerTransactions(customerPage).catch((error) => {
                  setApiErrors(error);
                  showError("Failed to load customer payment transactions");
                });
              }}
              onReview={(transaction) => {
                setSelectedCustomerTransaction(transaction);
                setIsCustomerModalOpen(true);
              }}
              onSplit={(transaction) => {
                setSelectedSplitTransaction(transaction);
                setSelectedSplitTransactionType("customer");
                setIsSplitModalOpen(true);
              }}
              onView={(transaction) => {
                setSelectedCustomerViewTransaction(transaction);
                setIsCustomerViewModalOpen(true);
              }}
              onPageChange={handleCustomerPageChange}
            />

            <CashBankTransactionSection
              ref={vendorPaymentsRef}
              title="Vendor Payments (Money Out)"
              type="debit"
              status={vendorStatus}
              onStatusChange={handleVendorStatusChange}
              searchTerm={vendorSearchTerm}
              onSearchTermChange={handleVendorSearchTermChange}
              sort={vendorSort}
              onSortChange={handleVendorSortChange}
              transactions={vendorTransactionList}
              pagination={vendorPagination}
              loading={smoothVendorListLoading}
              tableLoading={smoothVendorTableLoading}
              error={vendorTransactionsError}
              canMatch={canMatch}
              canUnmatch={canUnmatch}
              canSplit={canSplit}
              onRefresh={() => {
                setVendorLoadingReason("refresh");
                fetchVendorTransactions(vendorPage).catch((error) => {
                  setApiErrors(error);
                  showError("Failed to load vendor payment transactions");
                });
              }}
              onReview={(transaction) => {
                setSelectedVendorTransaction(transaction);
                setIsVendorModalOpen(true);
              }}
              onSplit={(transaction) => {
                setSelectedSplitTransaction(transaction);
                setSelectedSplitTransactionType("vendor");
                setIsSplitModalOpen(true);
              }}
              onView={(transaction) => {
                setSelectedVendorViewTransaction(transaction);
                setIsVendorViewModalOpen(true);
              }}
              onPageChange={handleVendorPageChange}
            />
          </>
        )}
      </div>

      <CustomerTransactionViewModal
        open={isCustomerViewModalOpen}
        transactionData={selectedCustomerViewTransaction}
        onOpenChange={(open) => {
          setIsCustomerViewModalOpen(open);
          if (!open) {
            setSelectedCustomerViewTransaction(null);
          }
        }}
        onTransactionUnmatched={handleCustomerUnmatched}
      />
      <VendorTransactionViewModal
        open={isVendorViewModalOpen}
        transactionData={selectedVendorViewTransaction}
        onOpenChange={(open) => {
          setIsVendorViewModalOpen(open);
          if (!open) {
            setSelectedVendorViewTransaction(null);
          }
        }}
        onTransactionUnmatched={handleVendorUnmatched}
      />
      <CustomerPaymentTransactionModal
        open={isCustomerModalOpen}
        onOpenChange={(open) => {
          setIsCustomerModalOpen(open);
          if (!open) {
            setSelectedCustomerTransaction(null);
          }
        }}
        zoho_bankaccountid={selectedZohoBankAccountId}
        transactionData={selectedCustomerTransaction}
        onTransactionMatched={handleCustomerMatched}
        onReviewRequestSubmitted={() =>
          fetchCustomerTransactions(customerPage).catch(() => {})
        }
        allBankAccounts={legacyBankAccounts}
        currentBankAccountId={selectedAccountId?.toString() || ""}
        currentBankAccountName={selectedAccount?.bank_name || ""}
        currentAccountType={getModalAccountType(selectedAccount)}
      />
      <VendorPaymentTransactionModal
        open={isVendorModalOpen}
        onOpenChange={(open) => {
          setIsVendorModalOpen(open);
          if (!open) {
            setSelectedVendorTransaction(null);
          }
        }}
        zoho_bankaccountid={selectedZohoBankAccountId}
        transactionData={selectedVendorTransaction}
        onTransactionMatched={handleVendorMatched}
        onReviewRequestSubmitted={() =>
          fetchVendorTransactions(vendorPage).catch(() => {})
        }
        allBankAccounts={legacyBankAccounts}
        currentBankAccountId={selectedAccountId?.toString() || ""}
        currentBankAccountName={selectedAccount?.bank_name || ""}
      />
      <AddBankAccountModal
        open={isAddAccountModalOpen}
        onOpenChange={setIsAddAccountModalOpen}
        onSuccess={refetchAccounts}
      />
      <SplitTransactionModal
        open={isSplitModalOpen}
        transaction={selectedSplitTransaction}
        onOpenChange={(open) => {
          setIsSplitModalOpen(open);
          if (!open) {
            setSelectedSplitTransaction(null);
            setSelectedSplitTransactionType(null);
          }
        }}
        onSubmit={handleSplitTransaction}
      />
      {splitReviewRequest && (
        <ReviewRequestModal
          open={isSplitReviewModalOpen}
          onOpenChange={(open) => {
            setIsSplitReviewModalOpen(open);
            if (!open) {
              setSplitReviewRequest(null);
            }
          }}
          moduleCode={MODULE_CODES.BANK_TRANSACTION}
          actionCode={ACTION_CODES.SPLIT_TRANSACTION_AMOUNT}
          entityType="bank_transaction"
          entityId={splitReviewRequest.transaction.id ?? 0}
          documentNumber={splitReviewRequest.transaction.transaction_id}
          skipNavigation
          transactionData={null}
          changeDetails={{
            action_types: [ACTION_CODES.SPLIT_TRANSACTION_AMOUNT],
            raw_json: {
              transaction_id: splitReviewRequest.transaction.transaction_id,
              transaction_date: splitReviewRequest.transaction.date,
              description: splitReviewRequest.transaction.description,
              transaction_amount: splitReviewRequest.transaction.amount,
              currency_symbol:
                splitReviewRequest.transaction.currency_symbol ?? "₹",
              currency_code: splitReviewRequest.transaction.currency_code,
              split_items: splitReviewRequest.splits.map((split) => ({
                amount: split.amount,
                description: split.description.trim(),
              })),
              split_count: splitReviewRequest.splits.length,
              reason: `Splitting its amount into ${splitReviewRequest.splits.length} items`,
            },
          }}
          onSuccess={() => {
            refreshSplitReviewTransactionList().catch(() => {});
          }}
          onError={(error) => {
            setApiErrors(error);
          }}
        />
      )}
    </div>
  );
};

export default CashBankSection;
